import React, { FC } from 'react';
import type { ChatMessage } from '../../types';
import { ReactComponent as CalendarIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/let-choose-time.svg';
import { ReactComponent as OnlineZoomIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/online-via-zoom.svg';
import { ReactComponent as FreeStarIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/free.svg';
import { ReactComponent as CallTimingClockIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/call-timing-clock.svg';
import './FreeCallInvitationBubble.scss';

interface FreeCallInvitationBubbleProps {
  message: ChatMessage;
  clientName?: string;
}

export const FreeCallInvitationBubble: FC<FreeCallInvitationBubbleProps> = ({
  message,
  clientName = 'Client',
}) => {
  const isConfirmed = message.call_status === 'confirmed';
  const clientFirstName = (clientName || message.receive_name || 'Client').split(' ')[0];

  const scheduleSummary = message.trainer_schedule_summary || 'Monday to Friday • 09:00 - 17:00';
  const timezone = message.trainer_timezone || 'Europe/London';
  const scheduleLine = `${scheduleSummary} (${timezone})`;

  const confirmedDate = message.call_date || '14 May 2026';
  const confirmedTime = message.call_time || '09:00 - 09:15';

  const hasDaysList =
    Array.isArray(message.trainer_schedule_days) && message.trainer_schedule_days.length > 0;

  return (
    <div className="free-call-invitation-bubble-card">
      {/* Status Badge */}
      <div className={`call-status-badge ${isConfirmed ? 'confirmed' : 'awaiting'}`}>
        <span className="status-dot" />
        <span className="status-text">{isConfirmed ? 'Confirmed' : 'Awaiting response'}</span>
      </div>

      {/* Title & Subtitle */}
      <div className="call-info-header">
        <h3 className="call-title">{'15-min introductory call'}</h3>
        <p className="call-subtitle">
          {"A chance to discuss your goals and see if we're a good fit"}
        </p>
      </div>

      <div className="call-divider" />

      {/* Badges Row */}
      <div className="call-badges-row">
        <div className="bubble-badge-pill">
          <FreeStarIcon className="badge-svg star-icon" />
          <span>{'Free'}</span>
        </div>
        <div className="bubble-badge-pill">
          <OnlineZoomIcon className="badge-svg zoom-icon" />
          <span>{'Online via Zoom'}</span>
        </div>
      </div>

      <div className="call-divider" />

      {/* Date & Time / Availability Details */}
      <div className="call-details-lines">
        {isConfirmed ? (
          <>
            <div className="detail-row">
              <CalendarIcon className="detail-svg-icon" />
              <span className="detail-text bold">{confirmedDate}</span>
            </div>
            <div className="detail-row">
              <CallTimingClockIcon className="detail-svg-icon clock-solid" />
              <span className="detail-text bold">{confirmedTime}</span>
            </div>
          </>
        ) : (
          <>
            <div className="detail-row">
              <CalendarIcon className="detail-svg-icon" />
              <span className="detail-text bold">
                {`${clientFirstName} will choose a time from your availability`}
              </span>
            </div>
            <div className={`detail-row ${hasDaysList ? 'multi-line' : ''}`}>
              <CallTimingClockIcon className="detail-svg-icon clock-solid" />
              {hasDaysList ? (
                <div className="bubble-schedule-days">
                  {message.trainer_schedule_days?.map((item) => (
                    <div key={item.day} className="bubble-day-row">
                      <span className="day-name">{item.day}:</span>
                      <span className="day-time">{item.time}</span>
                    </div>
                  ))}
                  <div className="bubble-tz">{`(${timezone})`}</div>
                </div>
              ) : (
                <span className="detail-text bold">{scheduleLine}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
